import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import { useMe } from "../api/me";
import AnalystTargetBadge from "../components/AnalystTargetBadge";
import CadenceDial from "../components/CadenceDial";
import DivergenceFlag from "../components/DivergenceFlag";
import DividendBadge from "../components/DividendBadge";
import EarningsBadge from "../components/EarningsBadge";
import Footer from "../components/Footer";
import OnboardingOverlay from "../components/OnboardingOverlay";
import PageHeader from "../components/PageHeader";
import RatingBadge from "../components/RatingBadge";
import TopNav from "../components/TopNav";
import { TIERS, TIER_META, type Tier } from "../lib/tier";

interface SymbolOut {
  id: number;
  ticker: string;
  exchange: string;
  name: string;
  currency: string;
  region: string;
  yahoo_symbol: string;
}

interface WatchlistItem {
  symbol: SymbolOut;
  tier: string;
}

interface Watchlist {
  id: number;
  name: string;
  items: WatchlistItem[];
  tier_counts: Record<string, number>;
  tier_limits: Record<string, number>;
}

interface Quote {
  yahoo_symbol: string;
  last_close: number | null;
  prev_close: number | null;
  day_change_pct: number | null;
  last_ts: string | null;
  spark: number[];
  score_medium: number | null;
  stance_medium: string | null;
  next_earnings_date: string | null;
  market_cap: number | null;
  score_7d_delta: number | null;
  price_7d_pct: number | null;
  dividend_yield: number | null;
  analyst_target_mean: number | null;
  analyst_count: number | null;
  analyst_recommendation: string | null;
}

function ScoreChip({ score }: { score: number | null | undefined }) {
  if (score === null || score === undefined) return null;
  const cls =
    score >= 65
      ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
      : score <= 35
      ? "bg-rose-500/15 text-rose-300 ring-rose-500/30"
      : "bg-slate-500/15 text-slate-400 ring-slate-500/30";
  return (
    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ring-1 ring-inset ${cls}`}>
      {score.toFixed(0)}
    </span>
  );
}

function fmtNum(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function Sparkline({ data, width = 96, height = 24 }: { data: number[]; width?: number; height?: number }) {
  if (data.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data
    .map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`)
    .join(" ");
  const up = data[data.length - 1] >= data[0];
  return (
    <svg width={width} height={height} className="inline-block align-middle" aria-hidden="true">
      <polyline fill="none" strokeWidth="1.5" stroke={up ? "#34d399" : "#f87171"} points={pts} />
    </svg>
  );
}


function useWatchlist() {
  return useQuery<Watchlist>({
    queryKey: ["watchlist"],
    queryFn: async () => (await api.get<Watchlist>("/watchlist")).data,
  });
}

function useQuotes(symbols: string[]) {
  return useQuery<Quote[]>({
    queryKey: ["quotes", symbols.slice().sort().join(",")],
    queryFn: async () => {
      if (!symbols.length) return [];
      const { data } = await api.get<Quote[]>("/quotes", { params: { symbols: symbols.join(",") } });
      return data;
    },
    enabled: symbols.length > 0,
    refetchInterval: 30_000,
  });
}

function useSymbolSearch(q: string) {
  return useQuery<SymbolOut[]>({
    queryKey: ["symsearch", q],
    queryFn: async () => (await api.get<SymbolOut[]>("/symbols/search", { params: { q } })).data,
    enabled: q.length >= 1,
    staleTime: 60_000,
  });
}

export default function Dashboard() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useMe();
  const wl = useWatchlist();
  // First-run users see a welcome overlay until /api/onboarding/prime succeeds.
  // `onboarded === false` (not undefined) — undefined means /me hasn't loaded
  // yet and we'd rather not flash the overlay before we know.
  const showOnboarding = me.data?.authenticated && me.data?.onboarded === false;
  const symbols = wl.data?.items.map((i) => i.symbol.yahoo_symbol) ?? [];
  const quotes = useQuotes(symbols);

  const [search, setSearch] = useState("");
  // The cadence cards double as a filter: when one is selected the watchlist
  // narrows to that tier and new adds default to it. null = no filter (show
  // all rows; new adds default to "tracked").
  const [tierFilter, setTierFilter] = useState<Tier | null>(null);
  const [tierError, setTierError] = useState<string | null>(null);
  const sym = useSymbolSearch(search);

  const counts = wl.data?.tier_counts ?? { live: 0, tracked: 0, idle: 0 };
  const limits = wl.data?.tier_limits ?? {
    live: TIER_META.live.defaultLimit,
    tracked: TIER_META.tracked.defaultLimit,
    idle: TIER_META.idle.defaultLimit,
  };

  const addTier: Tier = tierFilter ?? "tracked";

  const addItem = useMutation({
    mutationFn: async (yahoo_symbol: string) => api.post("/watchlist/items", { yahoo_symbol, tier: addTier }),
    onSuccess: () => {
      setTierError(null);
      qc.invalidateQueries({ queryKey: ["watchlist"] });
    },
    onError: (err: AxiosError<{ detail?: string }>) => {
      setTierError(err.response?.data?.detail || "Could not add ticker.");
    },
  });

  const removeItem = useMutation({
    mutationFn: async (yahoo_symbol: string) =>
      api.delete(`/watchlist/items/${encodeURIComponent(yahoo_symbol)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["watchlist"] }),
  });

  const quotesBySym = new Map(quotes.data?.map((q) => [q.yahoo_symbol, q]));

  // Filter the rendered list by selected cadence card. Quotes are still
  // fetched for every symbol so toggling filters is instant (no refetch).
  const displayedItems = (wl.data?.items ?? []).filter(
    (it) => !tierFilter || it.tier === tierFilter,
  );

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      {showOnboarding && <OnboardingOverlay />}
      <PageHeader />
      <TopNav />

      <section className="mb-4 rounded-2xl border border-slate-800 bg-slate-900 p-3 sm:mb-6 sm:p-4">
        <div className="mb-2 flex items-baseline gap-1.5">
          <span className="text-sm font-medium text-slate-300">Watchlist</span>
          {/* Native disclosure — mobile-friendly tooltip equivalent.
              The cadence cards intentionally don't show their description
              inline anymore; this is the explicit way to read it. */}
          <details className="group inline-block">
            <summary
              className="cursor-pointer list-none rounded-full px-1 text-[11px] text-slate-500 hover:text-slate-300"
              aria-label="What do Live, Tracked, and Idle mean?"
            >
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-700 leading-none group-open:border-sky-500/50 group-open:text-sky-300">?</span>
            </summary>
            <div className="absolute z-10 mt-1 w-64 rounded-lg border border-slate-700 bg-slate-950 p-3 text-[11px] text-slate-300 shadow-xl">
              <div className="mb-1.5 font-medium text-slate-400">Cadence buckets</div>
              <ul className="space-y-1.5">
                {TIERS.map((t) => {
                  const m = TIER_META[t];
                  return (
                    <li key={t} className="flex items-start gap-2">
                      <span className="mt-0.5"><CadenceDial tier={t} /></span>
                      <div className="min-w-0">
                        <div><span className="font-medium text-slate-100">{m.label}</span> <span className="text-slate-500">— max {m.defaultLimit}</span></div>
                        <div className="text-slate-500">{m.cadence} {m.llm ? "· daily analysis" : "· no auto-analysis"}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </details>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ticker or name (AAPL, ASML, Toyota…)"
          inputMode="search"
          autoComplete="off"
          className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2.5 text-base placeholder:text-slate-500 focus:border-sky-500 focus:outline-none sm:text-sm"
        />

        {/* Cadence cards: filter the watchlist + set the bucket for new adds.
            Click again to deselect (back to "All"). Always 3-up. */}
        <div className="mt-2 grid grid-cols-3 gap-1">
          {TIERS.map((t) => {
            const meta = TIER_META[t];
            const used = counts[t] ?? 0;
            const cap = limits[t] ?? meta.defaultLimit;
            const selected = tierFilter === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTierFilter(selected ? null : t)}
                title={`${meta.label} — ${meta.cadence}${meta.llm ? " · daily analysis" : " · no auto-analysis"}. ${meta.desc}`}
                className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-left transition ${
                  selected
                    ? "border-sky-500/60 bg-sky-500/10 ring-1 ring-sky-500/20"
                    : "border-slate-800 bg-slate-950 hover:border-slate-700"
                }`}
              >
                <CadenceDial tier={t} />
                <div className="min-w-0 flex-1 leading-tight">
                  <div className={`text-xs font-medium ${selected ? "text-slate-100" : "text-slate-300"}`}>
                    {meta.label}
                  </div>
                  <div className={`text-[11px] tabular-nums ${selected ? "text-sky-300" : "text-slate-400"}`}>
                    {used}/{cap}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {tierError && (
          <div className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-2.5 py-1.5 text-[11px] text-rose-200">
            {tierError}
          </div>
        )}

        {search && sym.data && sym.data.length > 0 && (
          <ul className="mt-2 max-h-60 overflow-y-auto rounded-md border border-slate-800 text-sm">
            {sym.data.map((s) => (
              <li
                key={s.id}
                className="flex cursor-pointer flex-col gap-0.5 border-b border-slate-800 px-3 py-2.5 last:border-b-0 hover:bg-slate-800 sm:flex-row sm:items-center sm:justify-between sm:gap-2"
                onClick={() => {
                  addItem.mutate(s.yahoo_symbol);
                  setSearch("");
                }}
              >
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-slate-200">{s.ticker}</span>
                  <span className="ml-2 text-slate-400">{s.name}</span>
                </div>
                <span className="text-[11px] text-slate-500 sm:text-xs">
                  {s.region} · {s.exchange}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
        {tierFilter && (
          <div className="flex items-center gap-2 border-b border-slate-800 bg-slate-950/40 px-4 py-2 text-[11px] text-slate-400">
            <CadenceDial tier={tierFilter} />
            <span>
              <span className="font-medium text-slate-200">{TIER_META[tierFilter].label}</span>
              <span className="text-slate-500"> — {TIER_META[tierFilter].cadence}{TIER_META[tierFilter].llm ? " · daily analysis" : " · no auto-analysis"}</span>
            </span>
          </div>
        )}
        {/* Mobile: card list. md+: table. */}
        <ul className="divide-y divide-slate-800 md:hidden">
          {displayedItems.length === 0 && (
            <li className="px-4 py-10 text-center text-sm text-slate-500">
              {(wl.data?.items.length ?? 0) === 0
                ? "Empty watchlist. Add a symbol above."
                : `No tickers in ${tierFilter ? TIER_META[tierFilter].label : "this filter"}.`}
            </li>
          )}
          {displayedItems.map((it) => {
            const q = quotesBySym.get(it.symbol.yahoo_symbol);
            const pct = q?.day_change_pct ?? null;
            const pctColor =
              pct === null ? "text-slate-500" : pct >= 0 ? "text-emerald-400" : "text-rose-400";
            return (
              <li
                key={it.symbol.id}
                onClick={() => navigate(`/s/${encodeURIComponent(it.symbol.yahoo_symbol)}`)}
                className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-slate-800/40"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-base text-slate-100">{it.symbol.ticker}</span>
                    <CadenceDial tier={it.tier} />
                    <RatingBadge score={q?.score_medium ?? null} short />
                    <ScoreChip score={q?.score_medium ?? null} />
                    <DivergenceFlag scoreDelta={q?.score_7d_delta ?? null} pricePct={q?.price_7d_pct ?? null} />
                    <EarningsBadge date={q?.next_earnings_date ?? null} />
                    <DividendBadge yield_={q?.dividend_yield ?? null} />
                    <AnalystTargetBadge
                      target={q?.analyst_target_mean ?? null}
                      lastClose={q?.last_close ?? null}
                      count={q?.analyst_count ?? null}
                    />
                  </div>
                  <div className="truncate text-xs text-slate-500">
                    {it.symbol.name} · {it.symbol.region} · {it.symbol.currency}
                  </div>
                </div>
                <div className="text-right">
                  <div className="tabular-nums text-slate-100">{fmtNum(q?.last_close ?? null)}</div>
                  <div className={`text-xs tabular-nums ${pctColor}`}>
                    {pct === null ? "—" : `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeItem.mutate(it.symbol.yahoo_symbol);
                  }}
                  aria-label={`Remove ${it.symbol.ticker}`}
                  className="ml-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-800 hover:text-rose-400"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                  </svg>
                </button>
              </li>
            );
          })}
        </ul>

        <table className="hidden w-full text-sm md:table">
          <thead className="bg-slate-950 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Ticker</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Region</th>
              <th className="px-4 py-2 text-right">Last</th>
              <th className="px-4 py-2 text-right">Δ %</th>
              <th className="px-4 py-2">Trend</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {displayedItems.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  {(wl.data?.items.length ?? 0) === 0
                    ? "Empty watchlist. Add a symbol above."
                    : `No tickers in ${tierFilter ? TIER_META[tierFilter].label : "this filter"}.`}
                </td>
              </tr>
            )}
            {displayedItems.map((it) => {
              const q = quotesBySym.get(it.symbol.yahoo_symbol);
              const pct = q?.day_change_pct ?? null;
              const pctColor =
                pct === null ? "text-slate-500" : pct >= 0 ? "text-emerald-400" : "text-rose-400";
              return (
                <tr
                  key={it.symbol.id}
                  onClick={() => navigate(`/s/${encodeURIComponent(it.symbol.yahoo_symbol)}`)}
                  className="cursor-pointer border-t border-slate-800 hover:bg-slate-800/40"
                >
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Link
                        to={`/s/${encodeURIComponent(it.symbol.yahoo_symbol)}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-mono text-slate-100 hover:text-sky-300"
                      >
                        {it.symbol.ticker}
                      </Link>
                      <CadenceDial tier={it.tier} />
                      <RatingBadge score={q?.score_medium ?? null} short />
                      <ScoreChip score={q?.score_medium ?? null} />
                      <EarningsBadge date={q?.next_earnings_date ?? null} />
                      <DividendBadge yield_={q?.dividend_yield ?? null} />
                      <AnalystTargetBadge
                        target={q?.analyst_target_mean ?? null}
                        lastClose={q?.last_close ?? null}
                        count={q?.analyst_count ?? null}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-300">{it.symbol.name}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">
                    {it.symbol.region} · {it.symbol.currency}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(q?.last_close ?? null)}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${pctColor}`}>
                    {pct === null ? "—" : `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`}
                  </td>
                  <td className="px-4 py-2.5">
                    <Sparkline data={q?.spark ?? []} />
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem.mutate(it.symbol.yahoo_symbol);
                      }}
                      aria-label={`Remove ${it.symbol.ticker}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-800 hover:text-rose-400"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                        <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <Footer note="Quotes refresh every 30s · Data: yfinance (delayed) · Research only" />
    </div>
  );
}
